import { useState, ImgHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
    /** Image source URL */
    src: string;
    /** Alt text (required for accessibility) */
    alt: string;
    /** Aspect ratio for container (e.g., "square", "video", "4/3") */
    aspectRatio?: 'square' | 'video' | '4/3' | '3/2' | 'auto';
    /** Whether to lazy load (default: true) */
    lazy?: boolean;
    /** Whether to show loading placeholder (default: true) */
    showPlaceholder?: boolean;
    /** Priority loading (for above-the-fold images) */
    priority?: boolean;
    /** Sizes attribute for responsive images */
    sizes?: string;
    /** Container class name */
    containerClassName?: string;
}

const aspectRatioClasses = {
    square: 'aspect-square',
    video: 'aspect-video',
    '4/3': 'aspect-[4/3]',
    '3/2': 'aspect-[3/2]',
    auto: '',
};

/**
 * Optimized Image Component
 *
 * Features:
 * - Lazy loading with native browser support
 * - Loading placeholder with skeleton animation
 * - Aspect ratio container to prevent CLS
 * - Responsive srcset (if CDN supports)
 * - WebP with fallback
 *
 * @example
 * <OptimizedImage
 *   src={product.imageUrl}
 *   alt={product.name}
 *   aspectRatio="square"
 *   sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
 * />
 */
export const OptimizedImage = ({
    src,
    alt,
    aspectRatio = 'auto',
    lazy = true,
    showPlaceholder = true,
    priority = false,
    sizes = '100vw',
    className,
    containerClassName,
    ...props
}: OptimizedImageProps) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);

    // Generate srcset if URL supports query params
    // This works with CDNs like Cloudinary, imgix, etc.
    const generateSrcSet = (baseUrl: string): string | undefined => {
        // Check if URL already has query params
        const separator = baseUrl.includes('?') ? '&' : '?';

        // Only generate srcset if the URL looks like it could support transformations
        // This is a simple heuristic - adjust based on your CDN
        if (baseUrl.includes('cloudinary') || baseUrl.includes('imgix') || baseUrl.includes('imagekit')) {
            return `
                ${baseUrl}${separator}w=400 400w,
                ${baseUrl}${separator}w=800 800w,
                ${baseUrl}${separator}w=1200 1200w
            `.trim();
        }

        return undefined;
    };

    const srcSet = generateSrcSet(src);

    if (hasError) {
        return (
            <div
                className={cn(
                    "bg-muted flex items-center justify-center text-muted-foreground/70",
                    aspectRatioClasses[aspectRatio],
                    containerClassName
                )}
                role="img"
                aria-label={alt}
            >
                <span className="text-sm">Image unavailable</span>
            </div>
        );
    }

    return (
        <div className={cn("relative overflow-hidden", aspectRatioClasses[aspectRatio], containerClassName)}>
            {/* Placeholder skeleton */}
            {showPlaceholder && !isLoaded && (
                <div
                    className="absolute inset-0 bg-muted animate-pulse"
                    aria-hidden="true"
                />
            )}

            {/* Actual image */}
            <img
                src={src}
                alt={alt}
                srcSet={srcSet}
                sizes={srcSet ? sizes : undefined}
                loading={priority ? 'eager' : (lazy ? 'lazy' : undefined)}
                decoding={priority ? 'sync' : 'async'}
                onLoad={() => setIsLoaded(true)}
                onError={() => setHasError(true)}
                className={cn(
                    "transition-opacity duration-300",
                    isLoaded ? "opacity-100" : "opacity-0",
                    className
                )}
                {...props}
            />
        </div>
    );
};

export default OptimizedImage;
